import type { Vec2, Vec3, Ring } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { transformPoint } from '../mesh/meshBuilder';
import { ensureCCW } from '../../geo/polygon';
import { triangulate } from '../../geo/triangulate';

/**
 * Closed, outward-facing building blocks on top of MeshBuilder.quad/polygon/prism.
 * Plan coordinates are (u, v): u along the frame's heading, v to its left, so a plan
 * point maps to the local frame as (u, y, -v).
 */

export type UvOf = (p: Vec3) => Vec2;

export interface Caps {
  readonly top: boolean;
  readonly bottom: boolean;
}

export interface RadialSpec {
  readonly center: Vec2;
  readonly radius: number;
  readonly segments: number;
  readonly phase: number;
}

const OPEN_BOTTOM: Caps = { top: true, bottom: false };

export function planPoint(u: number, y: number, v: number): Vec3 {
  return [u, y, -v];
}

export function subFrame(frame: Frame, u: number, v: number, yawOffset: number, y = 0): Frame {
  return { origin: transformPoint(planPoint(u, y, v), frame), yaw: frame.yaw + yawOffset };
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function unit(a: Vec3): Vec3 {
  const l = Math.hypot(a[0], a[1], a[2]);
  return [a[0] / l, a[1] / l, a[2] / l];
}

function newellNormal(points: readonly Vec3[]): Vec3 {
  let x = 0;
  let y = 0;
  let z = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    x += (a[1] - b[1]) * (a[2] + b[2]);
    y += (a[2] - b[2]) * (a[0] + b[0]);
    z += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return [x, y, z];
}

/** Emits a convex planar polygon wound so that its normal points along `outward`. */
export function orientedFace(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  points: readonly Vec3[],
  outward: Vec3,
  uvOf?: UvOf
): void {
  const ordered = dot(newellNormal(points), outward) >= 0 ? points : [...points].reverse();
  builder.polygon(style, frame, ordered, uvOf);
}

const SLIVER_AREA = 1e-9;

/**
 * Earcut triangles of a ring with holes, counter-clockwise in the ring's plane. Earcut
 * can return zero-area triangles along collinear runs (crow-step treads); they cover
 * nothing and are dropped.
 */
export function triangulateRing(outer: Ring, holes: readonly Ring[] = []): Vec2[][] {
  const tri = triangulate({ outer, holes });
  const at = (i: number): Vec2 => [tri.positions[i * 2]!, tri.positions[i * 2 + 1]!];
  const out: Vec2[][] = [];
  for (let t = 0; t < tri.indices.length; t += 3) {
    const [a, b, c] = [at(tri.indices[t]!), at(tri.indices[t + 1]!), at(tri.indices[t + 2]!)];
    const area = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    if (Math.abs(area) > SLIVER_AREA) out.push([a, b, c]);
  }
  return out;
}

/** Triangulates any simple plan ring (concave allowed) as a horizontal face at height y. */
export function horizontalCap(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ring: Ring,
  y: number,
  up: boolean
): void {
  const uv = (p: Vec3): Vec2 => [p[0], -p[2]];
  for (const tri of triangulateRing(ring)) {
    const pts = tri.map((q) => planPoint(q[0], y, q[1]));
    orientedFace(builder, style, frame, pts, [0, up ? 1 : -1, 0], uv);
  }
}

export function prismSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ring: Ring,
  y0: number,
  y1: number,
  caps: Caps = OPEN_BOTTOM
): void {
  const ccw = ensureCCW(ring);
  builder.prism(style, frame, ccw, y0, y1, { top: false, bottom: false, sides: true });
  if (caps.top) horizontalCap(builder, style, frame, ccw, y1, true);
  if (caps.bottom) horizontalCap(builder, style, frame, ccw, y0, false);
}

export function boxSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  min: Vec3,
  max: Vec3,
  caps: Caps = OPEN_BOTTOM
): void {
  const ring: Ring = [
    [min[0], -max[2]],
    [max[0], -max[2]],
    [max[0], -min[2]],
    [min[0], -min[2]],
  ];
  prismSolid(builder, style, frame, ring, min[1], max[1], caps);
}

/** Counter-clockwise plan ring of a regular polygon; vertex 0 sits at the phase angle. */
export function ringPoints(center: Vec2, radius: number, segments: number, phase: number): Ring {
  return Array.from({ length: segments }, (_, i): Vec2 => {
    const a = phase + (i / segments) * Math.PI * 2;
    return [center[0] + Math.cos(a) * radius, center[1] + Math.sin(a) * radius];
  });
}

export function frustumSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: RadialSpec,
  y0: number,
  y1: number,
  topRadius: number,
  caps: Caps = OPEN_BOTTOM
): void {
  const bottom = ringPoints(spec.center, spec.radius, spec.segments, spec.phase);
  const top = ringPoints(spec.center, topRadius, spec.segments, spec.phase);
  for (let i = 0; i < spec.segments; i++) {
    const j = (i + 1) % spec.segments;
    builder.quad(style, frame, [
      planPoint(bottom[i]![0], y0, bottom[i]![1]),
      planPoint(bottom[j]![0], y0, bottom[j]![1]),
      planPoint(top[j]![0], y1, top[j]![1]),
      planPoint(top[i]![0], y1, top[i]![1]),
    ]);
  }
  if (caps.top) horizontalCap(builder, style, frame, top, y1, true);
  if (caps.bottom) horizontalCap(builder, style, frame, bottom, y0, false);
}

export function coneSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: RadialSpec,
  y0: number,
  apexY: number,
  capBottom: boolean,
  baseStyle: VertexStyle = style
): void {
  const base = ringPoints(spec.center, spec.radius, spec.segments, spec.phase);
  const apex = planPoint(spec.center[0], apexY, spec.center[1]);
  for (let i = 0; i < spec.segments; i++) {
    const a = base[i]!;
    const b = base[(i + 1) % spec.segments]!;
    builder.polygon(style, frame, [planPoint(a[0], y0, a[1]), planPoint(b[0], y0, b[1]), apex]);
  }
  if (capBottom) horizontalCap(builder, baseStyle, frame, base, y0, false);
}

/** Square-section timber between two local points, closed at both ends. */
export function beamSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  from: Vec3,
  to: Vec3,
  halfWidth: number
): void {
  const axis = unit(sub(to, from));
  const helper: Vec3 = Math.abs(axis[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0];
  const s = unit(cross(axis, helper));
  const t = cross(axis, s);
  const offsets: readonly (readonly [number, number])[] = [
    [-1, -1],
    [1, -1],
    [1, 1],
    [-1, 1],
  ];
  const at = (p: Vec3, k: number): Vec3 => {
    const [a, b] = offsets[k]!;
    return [
      p[0] + (s[0] * a + t[0] * b) * halfWidth,
      p[1] + (s[1] * a + t[1] * b) * halfWidth,
      p[2] + (s[2] * a + t[2] * b) * halfWidth,
    ];
  };
  for (let k = 0; k < 4; k++) {
    const side = [at(from, k), at(from, (k + 1) % 4), at(to, (k + 1) % 4), at(to, k)];
    const [a, b] = offsets[k]!;
    const [c, d] = offsets[(k + 1) % 4]!;
    const out: Vec3 = [
      s[0] * (a + c) + t[0] * (b + d),
      s[1] * (a + c) + t[1] * (b + d),
      s[2] * (a + c) + t[2] * (b + d),
    ];
    orientedFace(builder, style, frame, side, out);
  }
  const ends = [0, 1, 2, 3];
  orientedFace(
    builder,
    style,
    frame,
    ends.map((k) => at(from, k)),
    [-axis[0], -axis[1], -axis[2]]
  );
  orientedFace(
    builder,
    style,
    frame,
    ends.map((k) => at(to, k)),
    axis
  );
}

/* A hexahedron's faces as corner indices, each in order round the face (hexahedronSolid). */
const HEXAHEDRON_FACES = [
  [0, 2, 6, 4],
  [1, 3, 7, 5],
  [0, 1, 5, 4],
  [2, 3, 7, 6],
  [0, 1, 3, 2],
  [4, 5, 7, 6],
] as const;

function centroid(points: readonly Vec3[]): Vec3 {
  const sum = points.reduce((a, p): Vec3 => [a[0] + p[0], a[1] + p[1], a[2] + p[2]], [0, 0, 0]);
  return [sum[0] / points.length, sum[1] / points.length, sum[2] / points.length];
}

/**
 * A closed convex hexahedron from its eight corners, as a box's from min to max: bits 0, 1 and 2 of a
 * corner's index choose the far end along its three edge directions. Each face turns away from the centre.
 */
export function hexahedronSolid(builder: MeshBuilder, style: VertexStyle, frame: Frame, corners: readonly Vec3[]): void {
  const centre = centroid(corners);
  for (const face of HEXAHEDRON_FACES) {
    const points = face.map((i) => corners[i]!);
    orientedFace(builder, style, frame, points, sub(centroid(points), centre));
  }
}

export function octahedronSolid(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  center: Vec3,
  radius: number
): void {
  const [x, y, z] = center;
  const top: Vec3 = [x, y + radius, z];
  const bottom: Vec3 = [x, y - radius, z];
  const rim: readonly Vec3[] = [
    [x + radius, y, z],
    [x, y, z - radius],
    [x - radius, y, z],
    [x, y, z + radius],
  ];
  for (let i = 0; i < 4; i++) {
    const a = rim[i]!;
    const b = rim[(i + 1) % 4]!;
    orientedFace(builder, style, frame, [a, b, top], sub(top, center));
    orientedFace(builder, style, frame, [a, b, bottom], sub(bottom, center));
  }
}

/**
 * Faces from every edge of a counter-clockwise plan ring up to one apex. Faces that
 * stand vertical (apex above the edge, e.g. the flat back of a half-round tower) are
 * walls and take `wallStyle`.
 */
export function pyramidOverRing(
  builder: MeshBuilder,
  styles: { readonly roof: VertexStyle; readonly wall: VertexStyle },
  frame: Frame,
  ring: Ring,
  y0: number,
  apex: { readonly plan: Vec2; readonly y: number }
): void {
  const ccw = ensureCCW(ring);
  const top = planPoint(apex.plan[0], apex.y, apex.plan[1]);
  for (let i = 0; i < ccw.length; i++) {
    const a = ccw[i]!;
    const b = ccw[(i + 1) % ccw.length]!;
    const du = b[0] - a[0];
    const dv = b[1] - a[1];
    const len = Math.hypot(du, dv);
    const reach = ((apex.plan[0] - a[0]) * dv - (apex.plan[1] - a[1]) * du) / len;
    const vertical = Math.abs(reach) < 1e-6;
    const out: Vec3 = [dv / len, vertical ? 0 : 1, du / len];
    const face = [planPoint(a[0], y0, a[1]), planPoint(b[0], y0, b[1]), top];
    orientedFace(builder, vertical ? styles.wall : styles.roof, frame, face, out);
  }
}
